import { useState, useEffect, useMemo } from 'react';

type CreditPackageWithExtensions = {
  pricesArray?: unknown[];
  plan_type?: string;
  translations?: Record<string, unknown>;
  nameTranslations?: Record<string, unknown>;
  descriptionTranslations?: Record<string, unknown>;
  featuresTranslations?: Record<string, unknown>;
};
import { Loader2, X, Plus, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { TranslationButton, TranslationDialog } from '@/components/dialogs/TranslationDialog';
import { FeaturesTranslationDialog } from '@/components/dialogs/FeaturesTranslationDialog';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  CreditPackage
} from '@/types/billing';
import { 
  useCreateCreditPackage, 
  useUpdateCreditPackage,
} from '@/hooks/billing';
import { 
  CreditPackageCreateData
} from '@/services/billing/credit-packages.service';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useTranslation } from 'react-i18next';

interface CreditPackageEditorProps {
  productId: string;
  creditPackage?: Partial<CreditPackage>;
  isOpen: boolean;
  onClose: () => void;
  activeCurrencies?: string[];
  defaultCurrency?: string;
  planType?: 'individual' | 'teams';
  supportedLanguages?: string[];
  currentLanguage?: string;
}

export function CreditPackageEditor({
  productId,
  creditPackage,
  isOpen,
  onClose,
  activeCurrencies = ['USD', 'EUR'],
  defaultCurrency = 'USD',
  planType = 'individual',
  supportedLanguages = ['en'], // Ready for translation implementation
  currentLanguage = 'en' // Ready for translation implementation
}: CreditPackageEditorProps) {
  const { t } = useTranslation();
  const isEditing = !!creditPackage?.id;
  
  // Debug: Log the supported languages
  console.log('CreditPackageEditor - supportedLanguages:', supportedLanguages);
  console.log('CreditPackageEditor - supportedLanguages length:', supportedLanguages?.length);
  
  // Translation params ready for use
  void currentLanguage;
  
  // Form state - initialize prices for all active currencies
  const initialPrices = useMemo(() => {
    return activeCurrencies.reduce((acc, currency) => {
      acc[currency] = 0;
      return acc;
    }, {} as { [key: string]: number });
  }, [activeCurrencies]);
  
  const [formData, setFormData] = useState<CreditPackageCreateData>({
    name: '',
    description: '',
    credits: 10000,
    prices: initialPrices,
    defaultCurrency,
    status: 'active',
    plan_type: planType
  });

  // Store price IDs for updating existing prices
  const [priceIds, setPriceIds] = useState<{ [currency: string]: string }>({});
  
  // Track original prices to detect changes
  const [originalPrices, setOriginalPrices] = useState<{ [key: string]: number }>({});

  // Feature flags state
  const [selectedFeatureFlags, setSelectedFeatureFlags] = useState<{ [key: string]: boolean }>({});
  const [features, setFeatures] = useState<string[]>([]);
  const [newFeature, setNewFeature] = useState('');
  
  // Additional settings state
  const [isRecommended, setIsRecommended] = useState(false);
  const [offerFreeTrial, setOfferFreeTrial] = useState(false);
  const [freeTrialDays, setFreeTrialDays] = useState(14);
  const [trialRequiresCard, setTrialRequiresCard] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  // Translation state
  const [showNameTranslationDialog, setShowNameTranslationDialog] = useState(false);
  const [showDescriptionTranslationDialog, setShowDescriptionTranslationDialog] = useState(false);
  const [showFeaturesTranslationDialog, setShowFeaturesTranslationDialog] = useState(false);
  const [nameTranslations, setNameTranslations] = useState<{ [lang: string]: string }>({});
  const [descriptionTranslations, setDescriptionTranslations] = useState<{ [lang: string]: string }>({});
  const [featuresTranslations, setFeaturesTranslations] = useState<{ [lang: string]: string[] }>({});
  
  // Fetch available feature flags
  const { data: featureFlagsResponse } = useFeatureFlags(productId);
  const availableFeatureFlags = featureFlagsResponse?.data || [];
  
  // Mutations
  const createMutation = useCreateCreditPackage();
  const updateMutation = useUpdateCreditPackage();

  // Initialize form data from creditPackage prop or reset for new package
  useEffect(() => {
    if (isOpen) {
      if (creditPackage) {
        // Handle both old format (prices as object) and new format (prices as array with IDs)
        let prices = initialPrices;
        const extractedPriceIds: { [currency: string]: string } = {};

        console.log('CreditPackageEditor - Full creditPackage object:', creditPackage);
        console.log('CreditPackageEditor - creditPackage.prices:', creditPackage.prices);
        const packageWithExt = creditPackage as unknown as CreditPackageWithExtensions;
        console.log('CreditPackageEditor - creditPackage.pricesArray:', packageWithExt.pricesArray);

        // Check for pricesArray first (contains IDs), then fallback to prices (legacy format)
        const pricesData = packageWithExt.pricesArray || creditPackage.prices;

        if (pricesData) {
          if (Array.isArray(pricesData)) {
            // New format: array of price objects with IDs
            console.log('CreditPackageEditor - Processing price array with IDs:', pricesData);
            type PriceObj = { id?: string; currency?: string; amount?: number };
            (pricesData as PriceObj[]).forEach((priceObj) => {
              const currency = priceObj.currency;
              const amount = priceObj.amount || 0;
              const id = priceObj.id;
              
              if (currency) {
                prices[currency] = amount;
                if (id) {
                  extractedPriceIds[currency] = id;
                  console.log(`CreditPackageEditor - Extracted price ID for ${currency}:`, id);
                }
              }
            });
          } else if (typeof pricesData === 'object') {
            // Old format: object with currency keys
            prices = pricesData;
          }
        }

        console.log('CreditPackageEditor - Final prices:', prices);
        console.log('CreditPackageEditor - Extracted price IDs:', extractedPriceIds);

        setFormData({
          name: creditPackage.name || '',
          description: creditPackage.description || '',
          credits: creditPackage.credits || 10000,
          prices: prices,
          defaultCurrency: creditPackage.defaultCurrency || defaultCurrency,
          status: creditPackage.status || 'active',
          plan_type: (packageWithExt.plan_type as 'individual' | 'teams' | undefined) || planType
        });

        // Store price IDs for updates
        setPriceIds(extractedPriceIds);
        
        // Store original prices to track changes
        setOriginalPrices({ ...prices });
        setSelectedFeatureFlags(creditPackage.featureFlags || {});
        setFeatures(creditPackage.features || []);
        // Handle trial days - backend returns trial_days (underscore format)
        const trialDays = creditPackage.trial_days || creditPackage.trialDays || creditPackage.freeTrialDays || 0;
        const requiresCard = creditPackage.trial_requires_card ?? creditPackage.trialRequiresCard ?? false;
        
        setIsRecommended(creditPackage.isRecommended ?? creditPackage.is_recommended ?? false);
        setOfferFreeTrial(trialDays > 0);
        setFreeTrialDays(trialDays || 14);
        setTrialRequiresCard(requiresCard);
        
        // Handle translations - check both new and old formats
        if (packageWithExt.translations) {
          // New format with nested translations object
          const trans = packageWithExt.translations;
          // Handle both camelCase (after deserialization) and snake_case
          const nameTransData = (trans?.displayName as Record<string, string>) || (trans?.display_name as Record<string, string>) || (trans?.name as Record<string, string>) || {};
          const descTransData = (trans?.description as Record<string, string>) || {};
          setNameTranslations((nameTransData || {}) as { [lang: string]: string });
          setDescriptionTranslations((descTransData || {}) as { [lang: string]: string });
          
          // Convert features array format to indexed format
          if (trans?.features && Array.isArray(trans.features)) {
            console.log('CreditPackageEditor - Features translations from API:', trans.features);
            const indexedFeatures: { [lang: string]: string[] } = {};
            trans.features.forEach((featureTrans: Record<string, string>, index: number) => {
              Object.keys(featureTrans).forEach(lang => {
                if (!indexedFeatures[lang]) {
                  indexedFeatures[lang] = [];
                }
                indexedFeatures[lang][index] = featureTrans[lang];
              });
            });
            console.log('CreditPackageEditor - Indexed features translations:', indexedFeatures);
            setFeaturesTranslations(indexedFeatures as unknown as { [lang: string]: string[] });
          } else {
            setFeaturesTranslations((trans?.features as unknown as { [lang: string]: string[] }) || {});
          }
        } else {
          // Old format with flat structure
          setNameTranslations((packageWithExt.nameTranslations || {}) as { [lang: string]: string });
          setDescriptionTranslations((packageWithExt.descriptionTranslations || {}) as { [lang: string]: string });
          setFeaturesTranslations((packageWithExt.featuresTranslations || {}) as { [lang: string]: string[] });
        }
      } else {
        // Reset form for new package
        setFormData({
          name: '',
          description: '',
          credits: 10000,
          prices: initialPrices,
          defaultCurrency,
          status: 'active',
          plan_type: planType
        });
        setSelectedFeatureFlags({});
        setFeatures([]);
        setNewFeature('');
        setIsRecommended(false);
        setOfferFreeTrial(false);
        setFreeTrialDays(14);
        setTrialRequiresCard(false);
        setValidationErrors({});
        setNameTranslations({});
        setDescriptionTranslations({});
        setFeaturesTranslations({});
        setPriceIds({}); // Reset price IDs for new package
        setOriginalPrices({}); // Reset original prices for new package
      }
    }
  }, [isOpen, creditPackage, defaultCurrency, initialPrices, planType]);

  const handleAddFeature = () => {
    if (newFeature.trim() && !features.includes(newFeature.trim())) {
      setFeatures([...features, newFeature.trim()]);
      setNewFeature('');
    }
  };

  const handleRemoveFeature = (feature: string) => {
    setFeatures(features.filter(f => f !== feature));
  };

  const handleFeatureFlagToggle = (flagKey: string) => {
    setSelectedFeatureFlags(prev => ({
      ...prev,
      [flagKey]: !prev[flagKey]
    }));
  };

  const handleCreditsChange = (value: string) => {
    const credits = parseInt(value) || 0;
    if (credits >= 0) {
      setFormData({ ...formData, credits });
    }
  };

  const handlePriceChange = (currency: string, value: string) => {
    const price = parseFloat(value) || 0;
    setFormData({
      ...formData,
      prices: {
        ...formData.prices,
        [currency]: price
      }
    });
    
    // Clear validation error for this currency when price is updated
    if (price > 0 && validationErrors[`price-${currency}`]) {
      const newErrors = { ...validationErrors };
      delete newErrors[`price-${currency}`];
      setValidationErrors(newErrors);
    }
  };

  const validatePricing = (): boolean => {
    const errors: Record<string, string> = {};
    let isValid = true;

    // Check each active currency
    activeCurrencies.forEach(currency => {
      const price = formData.prices?.[currency];
      
      if (!price || price <= 0) {
        errors[`price-${currency}`] = t('products.creditPackage.validation.priceForCurrency', { currency });
        isValid = false;
      }
    });

    setValidationErrors(errors);
    return isValid;
  };

  const handleSave = async () => {
    // Validate form
    if (!formData.name.trim()) {
      toast.error(t('products.creditPackage.validation.nameRequired'));
      return;
    }

    if (formData.credits <= 0) {
      toast.error(t('products.creditPackage.validation.creditsRequired'));
      return;
    }

    // Validate pricing - all currencies must have prices
    if (!validatePricing()) {
      toast.error(t('products.creditPackage.validation.pricesRequired'));
      return;
    }

    try {
      // Detect which prices have actually changed
      const changedPrices: { [currency: string]: number } = {};
      const changedPriceIds: { [currency: string]: string } = {};
      
      if (isEditing) {
        // For existing packages, only include changed prices
        Object.keys(formData.prices).forEach(currency => {
          const currentPrice = formData.prices[currency];
          const originalPrice = originalPrices[currency];
          
          // Include price if it's new or if it has changed
          if (originalPrice === undefined || currentPrice !== originalPrice) {
            changedPrices[currency] = currentPrice;
            // Include price ID only if this is an update (not a new price)
            if (originalPrice !== undefined && priceIds[currency]) {
              changedPriceIds[currency] = priceIds[currency];
            }
          }
        });
      } else {
        // For new packages, include all prices
        Object.assign(changedPrices, formData.prices);
      }

      console.log('CreditPackageEditor - Original prices:', originalPrices);
      console.log('CreditPackageEditor - Current prices:', formData.prices);
      console.log('CreditPackageEditor - Changed prices:', changedPrices);
      console.log('CreditPackageEditor - Changed price IDs:', changedPriceIds);

      // Prepare data for submission
      const submitData = {
        ...formData,
        name: formData.name.trim(),
        description: formData.description?.trim() || undefined,
        featureFlags: selectedFeatureFlags,
        features: features.length > 0 ? features : undefined,
        // Structure pricing data with selective updates
        // Only include prices that have actually changed
        pricing: {
          prices: changedPrices,
          priceIds: changedPriceIds
        },
        // Add translations in the new format
        translations: {
          name: Object.keys(nameTranslations).length > 0 ? nameTranslations : undefined,
          description: Object.keys(descriptionTranslations).length > 0 ? descriptionTranslations : undefined,
          features: Object.keys(featuresTranslations).length > 0 ? 
            features.map((_, index) => {
              const featureTranslations: { [locale: string]: string } = {};
              Object.keys(featuresTranslations).forEach(locale => {
                if (featuresTranslations[locale] && featuresTranslations[locale][index]) {
                  featureTranslations[locale] = featuresTranslations[locale][index];
                }
              });
              return featureTranslations;
            }) : undefined
        },
        isRecommended,
        // Use the backend's expected format for trial settings
        offerFreeTrial,
        freeTrialDays: offerFreeTrial ? freeTrialDays : undefined,
        trialRequiresCard: offerFreeTrial ? trialRequiresCard : undefined
      } as CreditPackageCreateData;

      console.log('CreditPackageEditor - Features state:', features);
      console.log('CreditPackageEditor - Price IDs being submitted:', priceIds);
      console.log('CreditPackageEditor - Submit data:', submitData);
      console.log('CreditPackageEditor - Features in submitData:', submitData.features);
      console.log('CreditPackageEditor - Pricing structure in submitData:', submitData.pricing);

      if (isEditing && creditPackage?.id) {
        await updateMutation.mutateAsync({
          productId,
          packageId: creditPackage.id,
          data: submitData
        });
      } else {
        await createMutation.mutateAsync({
          productId,
          data: submitData
        });
      }
      
      onClose();
    } catch (error) {
      console.error('Error saving credit package:', error);
      toast.error(t('products.creditPackage.toastSaveError'));
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#0a0a0a] text-white border-zinc-800">
        <DialogHeader>
          <div className="flex items-center justify-between mb-2">
            <DialogTitle className="text-lg font-normal">
              {t('products.creditPackage.dialogTitle')}
            </DialogTitle>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="h-6 w-6 rounded-sm hover:bg-zinc-800"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-sm text-zinc-400">
            {t('products.creditPackage.dialogDescription')}
          </p>
        </DialogHeader>

        <div className="space-y-6 pt-6">
          {/* Package Name and Description - Side by Side */}
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label htmlFor="name" className="text-sm font-normal text-white">
                  {t('products.creditPackage.nameLabel')}
                </Label>
                <TranslationButton
                  onClick={() => setShowNameTranslationDialog(true)}
                  translationStatus={
                    Object.keys(nameTranslations).length > 0 ? 'complete' : 'none'
                  }
                />
              </div>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('products.creditPackage.namePlaceholder')}
                className="bg-transparent border-zinc-700 text-white placeholder:text-zinc-600 h-9"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label htmlFor="description" className="text-sm font-normal text-white">
                  {t('products.creditPackage.descriptionLabel')}
                </Label>
                <TranslationButton
                  onClick={() => setShowDescriptionTranslationDialog(true)}
                  translationStatus={
                    Object.keys(descriptionTranslations).length > 0 ? 'complete' : 'none'
                  }
                />
              </div>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={t('products.creditPackage.descriptionPlaceholder')}
                className="bg-transparent border-zinc-700 text-white placeholder:text-zinc-600 h-9"
              />
            </div>
          </div>

          {/* Number of Credits */}
          <div className="space-y-2">
            <Label htmlFor="credits" className="text-sm font-normal text-white">
              {t('products.creditPackage.creditsLabel')}
            </Label>
            <Input
              id="credits"
              type="number"
              min="1"
              value={formData.credits}
              onChange={(e) => handleCreditsChange(e.target.value)}
              className="bg-transparent border-zinc-700 text-white h-9"
            />
            <p className="text-xs text-zinc-500">{t('products.creditPackage.creditsHelper')}</p>
          </div>

          {/* Package Price */}
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-normal text-white">{t('products.creditPackage.priceSectionTitle')}</h3>
              <p className="text-xs text-zinc-500 mt-0.5">{t('products.creditPackage.priceSectionDescription')}</p>
            </div>
            
            <div className="p-4 border border-zinc-700 rounded-lg space-y-4 bg-zinc-900/30">
              {activeCurrencies.map((currency) => {
                const price = formData.prices[currency] || 0;
                const hasError = validationErrors[`price-${currency}`];
                
                return (
                  <div key={currency} className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label className="text-sm text-white">{currency}</Label>
                      {currency === defaultCurrency && (
                        <span className="px-2 py-0.5 bg-zinc-800 text-xs rounded text-zinc-400">
                          {t('products.defaultBadgeLabel')}
                        </span>
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-zinc-400">
                          {currency === 'USD' ? '$' : currency === 'EUR' ? '€' : currency === 'GBP' ? '£' : currency}
                        </span>
                        <Input
                          type="number"
                          step="0.01"
                          value={price}
                          onChange={(e) => handlePriceChange(currency, e.target.value)}
                          placeholder={t('products.creditPackage.pricePlaceholder')}
                          className="bg-transparent text-white border-zinc-700"
                        />
                      </div>
                      {hasError && (
                        <p className="text-xs text-red-400 mt-1 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {hasError}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Features */}
          <div className="space-y-2">
            <div>
              <Label className="text-sm font-normal text-white">{t('products.creditPackage.featuresTitle')}</Label>
              <p className="text-xs text-zinc-500 mt-0.5">{t('products.creditPackage.featuresDescription')}</p>
            </div>
            
            <div className="flex items-center gap-2">
              <Input
                value={newFeature}
                onChange={(e) => setNewFeature(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddFeature())}
                placeholder={t('products.creditPackage.featurePlaceholder')}
                className="bg-transparent border-zinc-700 text-white placeholder:text-zinc-600 h-9 flex-1"
              />
              <Button
                onClick={handleAddFeature}
                variant="outline"
                size="sm"
                className="border-zinc-700 hover:bg-zinc-800 text-white h-9 px-3"
              >
                <Plus className="h-3 w-3 mr-1" />
                {t('common.add')}
              </Button>
              {features.length > 0 && (
                <TranslationButton
                  onClick={() => setShowFeaturesTranslationDialog(true)}
                  translationStatus={
                    Object.keys(featuresTranslations).length > 0 ? 'complete' : 'none'
                  }
                />
              )}
            </div>
            
            {features.length > 0 && (
              <div className="space-y-2 mt-2">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      value={feature}
                      readOnly
                      className="bg-transparent border-zinc-700 text-white h-9 flex-1"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveFeature(feature)}
                      className="h-9 w-9 text-zinc-400 hover:text-white hover:bg-zinc-800"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Feature Flags */}
          {availableFeatureFlags && availableFeatureFlags.length > 0 && (
            <div className="space-y-2">
              <div>
                <Label className="text-sm font-normal text-white">{t('products.creditPackage.featureFlagsTitle')}</Label>
                <p className="text-xs text-zinc-500 mt-0.5">{t('products.creditPackage.featureFlagsDescription')}</p>
              </div>
              
              <div className="space-y-2.5 border border-zinc-700 rounded-md p-4 bg-zinc-900/30">
                {availableFeatureFlags.map((flag) => (
                  <div key={flag.key} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        id={flag.key}
                        checked={selectedFeatureFlags[flag.key] || false}
                        onCheckedChange={() => handleFeatureFlagToggle(flag.key)}
                        className="border-zinc-600 data-[state=checked]:bg-primary data-[state=checked]:border-primary h-4 w-4"
                      />
                      <Label 
                        htmlFor={flag.key}
                        className="text-sm text-white font-normal cursor-pointer"
                      >
                        {flag.name || flag.key}
                      </Label>
                    </div>
                    <span className="text-xs text-zinc-500 font-mono">{flag.key}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Additional Settings */}
          <div className="space-y-4">
            <h3 className="text-sm font-normal text-white">{t('products.creditPackage.additionalSettingsTitle')}</h3>
            
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Switch
                  id="recommended"
                  checked={isRecommended}
                  onCheckedChange={setIsRecommended}
                  className="data-[state=checked]:bg-primary data-[state=unchecked]:bg-zinc-700"
                />
                <Label htmlFor="recommended" className="text-sm text-white font-normal cursor-pointer">
                  {t('products.creditPackage.recommendedLabel')}
                </Label>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Switch
                    id="free-trial"
                    checked={offerFreeTrial}
                    onCheckedChange={setOfferFreeTrial}
                    className="data-[state=checked]:bg-primary data-[state=unchecked]:bg-zinc-700"
                  />
                  <Label htmlFor="free-trial" className="text-sm text-white font-normal cursor-pointer">
                    {t('products.creditPackage.freeTrialLabel')}
                  </Label>
                </div>
                
                {offerFreeTrial && (
                  <div className="ml-12 space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="trial-days" className="text-xs text-zinc-400 font-normal">
                        {t('products.creditPackage.freeTrialDaysLabel')}
                      </Label>
                      <Input
                        id="trial-days"
                        type="number"
                        min="1"
                        max="365"
                        value={freeTrialDays}
                        onChange={(e) => setFreeTrialDays(parseInt(e.target.value) || 14)}
                        className="bg-transparent border-zinc-700 text-white h-9 w-32"
                      />
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <Checkbox
                        id="trial-requires-card"
                        checked={trialRequiresCard}
                        onCheckedChange={(checked) => setTrialRequiresCard(checked === true)}
                        className="border-zinc-600 data-[state=checked]:bg-primary data-[state=checked]:border-primary h-4 w-4"
                      />
                      <Label htmlFor="trial-requires-card" className="text-xs text-zinc-400 font-normal cursor-pointer">
                        {t('products.creditPackage.requireCardLabel')}
                      </Label>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="mt-6 flex justify-end gap-3">
          <Button 
            variant="outline" 
            onClick={onClose} 
            disabled={isLoading}
            className="border-zinc-600 bg-transparent hover:bg-zinc-800 text-white h-9 px-4 font-normal"
          >
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={isLoading}
            className="bg-white text-black hover:bg-zinc-100 h-9 px-4 font-normal"
          >
            {isLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {t('products.creditPackage.saveButton')}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Translation Dialogs */}
      <TranslationDialog
        open={showNameTranslationDialog}
        onOpenChange={setShowNameTranslationDialog}
        value={formData.name}
        translations={nameTranslations}
        onChange={setNameTranslations}
        fieldType="input"
        label={t('products.creditPackage.translationNameLabel')}
        supportedLanguages={supportedLanguages}
      />

      <TranslationDialog
        open={showDescriptionTranslationDialog}
        onOpenChange={setShowDescriptionTranslationDialog}
        value={formData.description || ''}
        translations={descriptionTranslations}
        onChange={setDescriptionTranslations}
        fieldType="input"
        label={t('products.creditPackage.translationDescriptionLabel')}
        supportedLanguages={supportedLanguages}
      />

      <FeaturesTranslationDialog
        open={showFeaturesTranslationDialog}
        onOpenChange={setShowFeaturesTranslationDialog}
        features={features}
        translations={featuresTranslations}
        onChange={setFeaturesTranslations}
        supportedLanguages={supportedLanguages}
      />
    </Dialog>
  );
}
